import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Accordion from '../components/Accordion';
import LightboxModal from '../components/LightboxModal';

export default function AboutBowtechPage() {
  const [faqs, setFaqs] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(null);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/faqs')
      .then((res) => res.json())
      .then((data) => {
        if (data?.items) setFaqs(data.items);
      })
      .catch(() => {});

    fetch('https://api.dev.auxo.dk/public/v2/content/galleries?slug.da=klinikken')
      .then((res) => res.json())
      .then((data) => {
        if (data?.items?.[0]?.images) {
          setGalleryImages(data.items[0].images);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <title>Om Bowtech | rolskov-bowtech.dk</title>
      <meta
        name="description"
        content="Læs om Bowtech hos Rolskov Bowtech. Dynamisk muskel- og bindevævsterapi der bringer kroppen i ro og genopretter balancen."
      />
      <link rel="canonical" href="https://rolskov-bowtech.dk/projects" />

      {/* Header Banner */}
      <section className="bg-[#f4efe6]/60 py-12 lg:py-16 border-b border-[#228A47]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <span className="text-xs font-semibold text-[#228A47] uppercase tracking-wider bg-[#e8f5ed] px-3 py-1 rounded-full">
            Faglig viden &amp; oprindelse
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#062364]">
            Om Bowtech
          </h1>
          <p className="text-base sm:text-lg text-[#132038] max-w-3xl leading-relaxed">
            Bowen teknikken (også kendt som Bowtech – den originale teknik) er et dynamisk system af muskel- og bindevævsterapi.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#faf8f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* Section 1: Hvad er Bowtech */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 space-y-5 bg-white p-8 rounded-2xl border border-[#228A47]/10 shadow-xs">
              <h2 className="text-2xl font-bold text-[#062364]">
                Hvad er Bowtech
              </h2>
              <ul className="space-y-3 text-sm text-[#132038] leading-relaxed">
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-leaf text-[#228A47] mt-1 shrink-0" aria-hidden="true"></i>
                  <span>
                    Bowen teknikken ( også kendt som Bowtech – den originale teknik ) er et dynamiske system af muskel- og bindevævsterapi, som blev udviklet af det nu afdøde Tom Bowen i Gelong – Australien.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-leaf text-[#228A47] mt-1 shrink-0" aria-hidden="true"></i>
                  <span>
                    Bowtech er en holistisk behandlingsform der bruger kroppens egen evne til at helbrede sig selv. I stedet for at fokusere på et enkelt symptom, henvender Bowtech sig til hele kroppen ved at genoprette kroppens balance via det autonome nervesystem.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-leaf text-[#228A47] mt-1 shrink-0" aria-hidden="true"></i>
                  <span>
                    Der bruges små lette tryk der stimulerer kroppen til at helbrede sig selv, ofte med et markant resultat.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-leaf text-[#228A47] mt-1 shrink-0" aria-hidden="true"></i>
                  <span>
                    Bowtech giver stor bedring og lindring for klienterne, med kun en ganske lille indsats af behandleren.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-leaf text-[#228A47] mt-1 shrink-0" aria-hidden="true"></i>
                  <span>
                    Behandlingen giver normalt en lettelse af både akutte – og kroniske skader.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <i className="fa-solid fa-leaf text-[#228A47] mt-1 shrink-0" aria-hidden="true"></i>
                  <span>
                    Bowtech er velegnet at bruge på alle mennesker i alle aldersgrupper og i forskellige helbredsstaider.
                  </span>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden shadow-md border border-[#228A47]/15 bg-white h-full">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/0424b756-a2fd-4ec3-9a16-f52eef89d933/8338.jpg"
                  alt="Blid Bowtech-behandling"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Hvordan foregår en behandling */}
          <div className="bg-[#e8f5ed] p-8 lg:p-10 rounded-3xl border border-[#228A47]/15 space-y-4">
            <h2 className="text-2xl font-bold text-[#062364]">
              Hvordan foregår en behandling
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-[#062364]">
              <div className="bg-white p-5 rounded-2xl border border-[#228A47]/10 space-y-2">
                <p className="font-semibold text-[#228A47]">Varighed &amp; greb</p>
                <p className="text-slate-700">
                  Behandlingen varer ca. 1 time og består af en eller flere procedurer med få eller flere greb. Grebene er blide men målrettede og kan, hvis det foretrækkes, udføres gennem let påklædning.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-[#228A47]/10 space-y-2">
                <p className="font-semibold text-[#228A47]">Korte pauser</p>
                <p className="text-slate-700">
                  Mellem hver serie af greb holder behandleren en pause (2 min.) for at kroppen kan få lov at reagere.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-[#228A47]/10 space-y-2">
                <p className="font-semibold text-[#228A47]">Akutte &amp; kroniske skader</p>
                <p className="text-slate-700">
                  Nogle skader/spændinger kan afhjælpes i løbet af 1 - 2 behandlinger. Gamle og kroniske skader har ofte behov for flere behandlinger.
                </p>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-[#228A47]/10 space-y-2">
                <p className="font-semibold text-[#228A47]">Kombination med andre behandlinger</p>
                <p className="text-slate-700">
                  Andre former for manipulerende behandling udført op til 4 dage før og 5 dage efter en Bowtechbehandling forstyrrer dens effektivitet.
                </p>
              </div>
            </div>
          </div>

          {/* Section 3: Hvad virker Bowtech på */}
          <div className="bg-white p-8 lg:p-10 rounded-3xl border border-[#228A47]/15 shadow-xs space-y-6">
            <h2 className="text-2xl font-bold text-[#062364]">
              Hvad virker Bowtech på
            </h2>
            <p className="text-sm text-[#132038]">
              Bowtech har gode resultater ved f.eks.:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm font-semibold text-[#062364]">
              {[
                'Sportsskader',
                'Nakke-/skulderproblemer (f.eks. ”frossen skulder”)',
                'Knæ- / ankelproblemer',
                'Ryg - / iskiassmerter',
                'Migræne og hovedpine',
                'Betændelsestilstande',
                'Stress og spændinger',
                'Skævheder i bevægeapparatet',
                'Allergi',
                'Fordøjelsesproblemer'
              ].map((symptom, i) => (
                <div key={i} className="flex items-center gap-3 p-3.5 bg-[#faf8f5] rounded-xl border border-[#228A47]/10">
                  <i className="fa-solid fa-circle-check text-[#228A47] text-base shrink-0" aria-hidden="true"></i>
                  <span>{symptom}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gallery Grid */}
          {galleryImages.length > 0 && (
            <div className="space-y-6">
              <div className="border-b border-[#228A47]/10 pb-4">
                <h3 className="text-xl font-bold text-[#062364]">
                  Galleribilleder fra klinikken
                </h3>
                <p className="text-xs text-slate-500">
                  Klik på et billede for at se det i stor visning.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveLightboxIndex(idx)}
                    className="group relative rounded-xl overflow-hidden aspect-4/3 border border-[#228A47]/10 bg-white focus:outline-none focus:ring-2 focus:ring-[#228A47]"
                  >
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                      alt={`Klinikbillede ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-[#062364]/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <i className="fa-regular fa-magnifying-glass-plus text-xl" aria-hidden="true"></i>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Accordion */}
          {faqs.length > 0 && (
            <div className="space-y-6 pt-4">
              <div className="space-y-1">
                <span className="text-xs font-semibold text-[#228A47] uppercase tracking-wider">
                  Spørgsmål &amp; Svar
                </span>
                <h2 className="text-2xl font-bold text-[#062364]">
                  Hyppigt stillede spørgsmål om Bowtech
                </h2>
              </div>
              <Accordion items={faqs} />
            </div>
          )}

          {/* Lightbox Modal */}
          <LightboxModal
            images={galleryImages}
            activeIndex={activeLightboxIndex}
            onClose={() => setActiveLightboxIndex(null)}
            onSelectIndex={(i) => setActiveLightboxIndex(i)}
          />

          {/* CTA Box */}
          <div className="bg-[#062364] text-white p-8 sm:p-10 rounded-3xl text-center space-y-4">
            <h3 className="text-2xl font-bold">Ønsker du mere information?</h3>
            <p className="text-sm text-slate-200 max-w-xl mx-auto">
              Kontakt Jørn Rolskov direkte på telefon eller mail for at drøfte din situation.
            </p>
            <div className="pt-2 flex flex-wrap justify-center gap-4">
              <a
                href="tel:27117557"
                className="whitespace-nowrap px-6 py-3 text-sm font-semibold text-white bg-[#228A47] hover:bg-[#1b713a] rounded-xl transition-colors"
              >
                Ring på 27 11 75 57
              </a>
              <Link
                to="/contact"
                className="px-6 py-3 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                Gå til kontaktsiden
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
